import { RelatedVariant } from "@interfaces/cart";

export const debounce = (func: Function, wait: number) => {
  let timeout: ReturnType<typeof setTimeout>;
  const debounced = function (this: any, ...args: any[]) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
};

export const handlelize = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');

export const handleAddToCart = (
  variantId: number | string,
  quantity?: number,
  relatedVariants?: RelatedVariant[]
) => {
  // Check if variantId matches the pattern 'gid://shopify/ProductVariant/1234567890'
  const numericIdMatch = String(variantId).match(/gid:\/\/shopify\/ProductVariant\/(\d+)/);

  // Extract the numeric ID or fallback to the original variantId
  const numericId = numericIdMatch ? numericIdMatch[1] : variantId;

  const addToCartEvent = new CustomEvent('cart:add', {
    detail: {
      payload: [
        {
          id: numericId,
          quantity: quantity && quantity > 1 ? quantity : 1,
          ...(relatedVariants && relatedVariants.length > 0
            ? { relatedVariants: JSON.stringify(relatedVariants) }
            : {}),
        },
      ],
    },
  });

  document.dispatchEvent(addToCartEvent);
};

export const truncateString = (str: string, maxLength: number) => {
  return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
}
export const cleanLabel = (label: string): string => {
  // Remove any prefix ending with "::"
  return label.includes("::") ? label.split("::")[1] : label;
};

export const formatAmount = (priceInCents: number): string => {
  return (priceInCents / 100).toFixed(2);
};

export const formatMoney = (
  { amount, currencyCode = 'USD' }: { amount: string | number; currencyCode?: string },
  cents = false
) => {
  let amt = Number(amount);

  if (Number.isNaN(amt)) {
    console.error('No amount value passed.');
    return '';
  }

  if (cents) amt /= 100;

  // Fallback to 'USD' if currencyCode is invalid
  const validCurrencyCode = currencyCode && typeof currencyCode === 'string' && currencyCode.trim() ? currencyCode : 'USD';

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: validCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(amt);
  } catch (error) {
    console.error(`Invalid currency code: ${validCurrencyCode}`, error);
    return amt.toFixed(2); // Fallback to a simple number formatting
  }
};

export const handleKlaviyoBIS = async (
  klaviyoAPIKey: string,
  variantID: number | string,
  email: string,
  emailList: string
) => {
  try {
    const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoAPIKey}`, {
      method: 'POST',
      headers: {
        'accept': 'application/vnd.api+json',
        'revision': '2025-01-15',
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            },
            "channels": ["EMAIL"],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantID}`
              }
            }
          }
        }
      })
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.error('Subscription failed:', error);
  }

  if(window.Shopify.country === 'US' || window.Shopify.country === 'CA'){
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${klaviyoAPIKey}`, {
        method: 'POST',
        headers: {
          'accept': 'application/vnd.api+json',
          'revision': '2025-01-15',
          'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED'
                        }
                      }
                    }
                  }
                }
              },
              "custom_source": "Back in Stock"
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: emailList
                }
              }
            }
          }
        })
      });
      if(!res.ok){
        throw new Error(`Response status: ${res.status}`);
      }
    } catch (error) {
      console.error('Subscription failed:', error);
    }
  }
  else {
    return false
  }

};

export const handleAddToKlaviyoList = async(
  email: string,
  klaviyoListId: string,
  klaviyoAPIKey: string
) => {
  const options = {
    method: 'POST',
    headers: {revision: '2024-10-15', 'content-type': 'application/vnd.api+json', 'Authorization': `${klaviyoAPIKey}`},
    body: `{"data":{"type":"subscription","attributes":{"profile":{"data":{"type":"profile","attributes":{"properties":{"newKey":"New Value"},"email":"${email}"},"meta":{"patch_properties":{"append":{"newKey":"New Value"},"unappend":{"newKey":"New Value"}}}}}},"relationships":{"list":{"data":{"type":"list","id":"${klaviyoListId}"}}}}}`
  };
  try {
    const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${klaviyoAPIKey}`, options)
    if(!response.ok){
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.error('Subscription failed:', error);
  }
}
